import { useEffect, useRef, useState, useCallback } from 'react';
import { UI_CONSTANTS } from '@petmas/shared';

interface UseScrollRevealOptions {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
}

/**
 * Hook for scroll-triggered reveal animations using Intersection Observer
 *
 * Usage:
 * ```tsx
 * function MyComponent() {
 *   const { ref, isRevealed } = useScrollReveal();
 *   return (
 *     <div ref={ref} className={`scroll-reveal ${isRevealed ? 'revealed' : ''}`}>
 *       Content that reveals on scroll
 *     </div>
 *   );
 * }
 * ```
 */
export function useScrollReveal<T extends HTMLElement = HTMLDivElement>(
  options: UseScrollRevealOptions = {}
) {
  const { threshold = 0.1, rootMargin = '0px 0px -50px 0px', triggerOnce = true } = options;
  const ref = useRef<T>(null);
  const [isRevealed, setIsRevealed] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsRevealed(true);
            if (triggerOnce) {
              observer.unobserve(element);
            }
          } else if (!triggerOnce) {
            setIsRevealed(false);
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [threshold, rootMargin, triggerOnce]);

  return { ref, isRevealed };
}

/**
 * Hook for multiple elements with staggered reveal animations
 *
 * Usage:
 * ```tsx
 * function MyList() {
 *   const { containerRef, getItemProps } = useStaggeredReveal({ count: items.length });
 *   return (
 *     <div ref={containerRef}>
 *       {items.map((item, index) => (
 *         <div key={item.id} {...getItemProps(index)}>
 *           {item.content}
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useStaggeredReveal(options: {
  count: number;
  staggerDelay?: number;
  threshold?: number;
  rootMargin?: string;
}) {
  const { count, staggerDelay = 100, threshold = 0.1, rootMargin = '0px 0px -50px 0px' } = options;
  const containerRef = useRef<HTMLDivElement>(null);
  const [revealedItems, setRevealedItems] = useState<Set<number>>(new Set());

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Reveal items with stagger
            for (let i = 0; i < count; i++) {
              setTimeout(() => {
                setRevealedItems((prev) => new Set([...prev, i]));
              }, i * staggerDelay);
            }
            observer.disconnect();
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [count, staggerDelay, threshold, rootMargin]);

  const getItemProps = useCallback(
    (index: number) => ({
      className: `scroll-reveal ${revealedItems.has(index) ? 'revealed' : ''}`,
      style: { transitionDelay: `${index * staggerDelay}ms` },
    }),
    [revealedItems, staggerDelay]
  );

  return { containerRef, revealedItems, getItemProps };
}

/**
 * Hook for counting up numbers when element enters viewport
 *
 * Usage:
 * ```tsx
 * function Stats() {
 *   const { ref, value } = useCountUp({ end: 1000, duration: 2000 });
 *   return <span ref={ref} className="count-up">{value}</span>;
 * }
 * ```
 */
export function useCountUp(options: {
  end: number;
  start?: number;
  duration?: number;
  threshold?: number;
}) {
  const { end, start = 0, duration = 2000, threshold = 0.5 } = options;
  const ref = useRef<HTMLSpanElement>(null);
  const [value, setValue] = useState(start);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || hasStarted) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasStarted) {
            setHasStarted(true);
            observer.disconnect();

            const startTime = globalThis.performance.now();
            const animate = (currentTime: number) => {
              const elapsed = currentTime - startTime;
              const progress = Math.min(elapsed / duration, 1);

              // Easing function (ease-out)
              const easeOut = 1 - Math.pow(1 - progress, UI_CONSTANTS.MAX_SUGGESTIONS_LIMIT);
              const currentValue = Math.round(start + (end - start) * easeOut);

              setValue(currentValue);

              if (progress < 1) {
                globalThis.requestAnimationFrame(animate);
              }
            };

            globalThis.requestAnimationFrame(animate);
          }
        });
      },
      { threshold }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [end, start, duration, threshold, hasStarted]);

  return { ref, value };
}

export default useScrollReveal;
